import React, {useState, useCallback, useEffect, useRef} from 'react';
import styles from '../Patient.module.css';
import PatientDetailDropdownCard from '../../../components/PatientDetailDropdownCard';
import QuestionnaireDetails from '../CardDetails/QuestionnaireDetails';
import { useSelector, useDispatch } from 'react-redux';
import QuestionnaireScoreCardView from '../modality-charts/QuestionnaireScoreCardView';
import useAxios from '../../../services/AxiosConfig';
import { setFilterItem } from '../Slices/PatientDetailFilterSlice';
import CustomToggle from '../../../components/CustomToggle';
import { SpecialtySelectedIcon } from '../../../components/icons/Icons';
import inputStyles from '../../ManagementPages.module.css';
import { formatDateString, formatDateStringWithoutYearAndTime } from '../../../utils/dateUtils';
import DateRangePicker from '../../tasks/DateRangePicker';
import {
    startOfDay,
    endOfDay
} from "date-fns";

const QuestionnaireView = () => {

    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const patientId = useSelector((state) => state.patient.id);
    const dispatch = useDispatch();
    const dateFormat = useSelector((state) => state.userInfo.dateFormat);

    const questionnaireIds = useSelector((state)=>state.patient.questionnaireIds);
    const [questionnaireData, setQuestionnaireData] = useState([]);
    const [missedData, setMissedData] = useState([]);
    const qFilter = useSelector((state)=>state.patientDetailFilter.questionnaireFilter);
    const startDate = useSelector((state) => state.patientDetailFilter.questionnaireStartDate);
    const endDate = useSelector((state) => state.patientDetailFilter.questionnaireEndDate);
    const timePeriod = useSelector((state) => state.patientDetailFilter.questionnaireTimePeriod);

    const [opacity, setOpacity] = useState({});
    const [showDatePopover, setShowDatePopover] = useState(false);
    const customButtonRef = useRef(null);

    const [listType, setListType] = useState("Completed");


    const PeriodFilters = [
        { label: "Week", value: "Week" },
        { label: "Month", value: "Month" },
        { label: "Custom", value: "Custom" },
    ];

    const convertDisplayDate = (dateStr) => {
        const date = new Date(dateStr);
        return formatDateStringWithoutYearAndTime(date.toISOString(), dateFormat);
    };

    const fetchQuestionnaireData = useCallback(() => {
        setQuestionnaireData([]);
        const fetchData = async () => {
            // for each questionnaire, fetch responses and save score + completed date
            const responsePromises = questionnaireIds.map(async (q) => {
            try {
                const responses = await axios.get(`/questionnaire/responses/list/${q.questionnaireId}/${patientId}?startDate=${startDate}&endDate=${endDate}`);
                if (Array.isArray(responses.data.data)) {
                return responses.data.data.map(response => {
                    let score = 0;
                    response.answers.forEach(a => {
                        score += a.score;
                    })
                    let retVal = response;
                    retVal["questionnaireId"] = q.questionnaireId;
                    retVal["score"] = score;
                    retVal["possibleScore"] = q.totalPossibleScore;
                    retVal["qDate"] = convertDisplayDate(response.completedDate);
                    retVal["sortDate"] = response.completedDate;
                    return retVal;
                });
                } 
                return null;
            } catch (err) {
                console.log(err);
                return [];
            }
            });
            let qWithResponses = await Promise.all(responsePromises);
            qWithResponses = qWithResponses.flat();
            qWithResponses.sort((a, b) => new Date(a.completedDate) - new Date(b.completedDate));
            setQuestionnaireData(qWithResponses);
            // fetch all missed responses for time period
            let missedResponses = [];
            try {
                const responses = await axios.get(`/questionnaire/missedResponses/list/${patientId}?startDate=${startDate}&endDate=${endDate}`);
                if (Array.isArray(responses.data.data)) {
                    missedResponses = responses.data.data.map(missed => {
                        let retVal = missed;
                        retVal[`missed.score`] = -1;
                        retVal["qDate"] = convertDisplayDate(missed.dueDate);
                        retVal["sortDate"] = missed.dueDate;
                        return retVal;
                    });
                } 
            } catch (err) {
                console.log(err);
            }
            missedResponses.sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
            setMissedData(missedResponses);

            setOpacity(questionnaireIds.reduce((acc, q) => {
                acc[q.name] = 1;
                return acc;
            }, {}));
        };

        fetchData();
    }, [startDate, endDate, axios, patientId, dateFormat, questionnaireIds]);

    useEffect(() => {
        fetchQuestionnaireData();
    }, [fetchQuestionnaireData]);

    useEffect(() => {
        if (questionnaireData.length > 0) {
            const updatedData = questionnaireData.map(q => ({
                ...q,
                qDate: convertDisplayDate(q.completedDate)
            }));
            setQuestionnaireData(updatedData);
        }
        if (missedData.length > 0) {
            const updatedMissed = missedData.map(q => ({
                ...q,
                qDate: convertDisplayDate(q.dueDate)
            }));
            setMissedData(updatedMissed);
        }
    }, [dateFormat]);

    const handleDateOrderChange = (value) => {

        const date = new Date();
        const today = new Date(date);
        today.setDate(today.getDate() + 1);
        let startDate, endDate;

        switch (value) {
            case 'Week':
                endDate = endOfDay(today);
                let weekStart =  new Date(today);
                weekStart.setDate(today.getDate() - 7);
                startDate = startOfDay(weekStart);
                break;
            case 'Month':
                endDate = endOfDay(today);
                let monthStart =  new Date(today);
                monthStart.setDate(today.getDate() - 30);
                startDate = startOfDay(monthStart);
                break;
            default:
                endDate = endOfDay(today);
                let defaultWeekStart =  new Date(today);
                defaultWeekStart.setDate(today.getDate() - 7);
                startDate = startOfDay(defaultWeekStart);
                break;
        }

        startDate = startDate.toISOString();
        endDate = endDate.toISOString();

        dispatch(setFilterItem({ key: "questionnaireTimePeriod", value: value }));
        dispatch(setFilterItem({ key: "questionnaireStartDate", value: startDate }));
        dispatch(setFilterItem({ key: "questionnaireEndDate", value: endDate }));
    };

    const handleDateRangeSave = (startDate, endDate) => {
        let start, end;
        if (startDate && endDate) {
            start = new Date(startDate);
            end = new Date(endDate);
        } else if (startDate) {
            start = new Date(startDate);
            end = new Date(startDate);
        } else if (endDate) {
            start = new Date(endDate);
            end = new Date(endDate);
        } else {
            return;
        }

        const formattedStartDate =start.toISOString();
        const formattedEndDate = end.toISOString();
    
        dispatch(setFilterItem({ key: "questionnaireTimePeriod", value: "Custom" }));
        dispatch(setFilterItem({ key: "questionnaireStartDate", value: formattedStartDate }));
        dispatch(setFilterItem({ key: "questionnaireEndDate", value: formattedEndDate }));
    };

    const handleToggle = (value) => {
        dispatch(setFilterItem({ key: "questionnaireFilter", value: value }))
    };

    const handleCustomDateClick = (event) => {
        event.stopPropagation();
        setShowDatePopover(true);
    };

    const descendingQData = [...questionnaireData].reverse();

    return (
        <div className={styles.ViewContainer}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", columnGap: "15px", alignItems: "center" }}>
                    <CustomToggle labels={[{value:"timeline", name:"Timeline"}, {value:"list", name:"List"}]} selectedToggle={qFilter} handleToggle={handleToggle} />
                    {qFilter === "list" && (
                        <div style={{position: "relative"}}>
                            <span className="floating-label">List Type</span>
                            <select 
                                className={inputStyles["styled-input-select"]}
                                style={{width:"141px"}}
                                onChange={(e) => setListType(e.target.value)}
                                value={listType}
                            >
                                <option value="Completed">Completed</option>
                                <option value="Missed">Missed</option>
                            </select>
                        </div>
                    )}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    <div className={styles.GraphPeriodButtons}>
                        {PeriodFilters.map((filter, index) => (
                            <div key={index} 
                                className={timePeriod === filter.value ? styles.TimePeriodSelected : ''} 
                                ref={filter.value === 'Custom' ? customButtonRef : null}
                                onClick={(event) => {
                                    if (filter.value === 'Custom') {
                                        handleCustomDateClick(event);
                                    } else {
                                        handleDateOrderChange(filter.value);
                                        setShowDatePopover(false);
                                    }
                                }}
                                >
                                {timePeriod === filter.value && <SpecialtySelectedIcon />}
                                {filter.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className={styles.PatientViewScrollContainer}>
                {qFilter === "timeline" &&
                    <div>
                        <QuestionnaireScoreCardView data={questionnaireData} opacity={opacity} setOpacity={setOpacity}/>
                    </div>
                }
                {qFilter === "list" && (
                    listType === "Completed" ? 
                    descendingQData.map((q, index) => {
                        let label = {
                            name:"Completed",
                            color: "#1F5C29"
                        };
                        const formattedDate = formatDateString(new Date(q.completedDate).toISOString(), dateFormat);
                        return (
                            <div key={index}>
                                <PatientDetailDropdownCard 
                                    title={q.name} 
                                    label={label}
                                    subtitle2={formattedDate}
                                    cardInfo={<QuestionnaireDetails questionnaire={q}/>}
                                />
                            
                            </div>
                        )
                    })
                    : missedData.map((q, index) => {
                        let label = {
                            name:"Missed",
                            color: "#630F0F"
                        };
                        const formattedDate = formatDateString(new Date(q.dueDate).toISOString(), dateFormat);
                        return (
                            <div key={index}>
                                <PatientDetailDropdownCard 
                                    title={q.taskName} 
                                    label={label}
                                    subtitle2={formattedDate}
                                    cardInfo={<QuestionnaireDetails questionnaire={q}/>}
                                />
                            
                            </div>
                        )
                    })
                )
                }
            </div>
            <DateRangePicker 
                onSave={handleDateRangeSave}
                onClose={() => setShowDatePopover(false)}
                mode={"modal"}
                showModal={showDatePopover}
            />
        </div>
    );
}

export default QuestionnaireView;